import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff, LogIn, Copy } from 'lucide-react';

const DEMO_ACCOUNTS = [
    { label: 'Admin Account', email: 'admin@crm.com', password: 'Admin123!', role: 'Admin', desc: 'Full CRM access with admin privileges (demo org)' },
    { label: 'Sales Rep Account', email: 'john.smith@crm.com', password: 'Rep123!', role: 'Sales Rep', desc: 'Standard sales representative view' },
];

export default function DemoLogin() {
    const [loading, setLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleLogin = async (email, password) => {
        setLoading(true);
        try {
            await login(email, password);
            toast.success('Logged in to demo');
            navigate('/dashboard');
        } catch {
            toast.error('Login failed');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-lg">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100/80 text-amber-800 text-xs font-semibold mt-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        DEMO ACCESS
                    </div>
                    <p className="text-stone-500 mt-3 text-sm">Choose an account below to explore the platform</p>
                </div>

                <div className="space-y-4" data-testid="demo-accounts">
                    {DEMO_ACCOUNTS.map((acc, i) => (
                        <div key={i} className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm" data-testid={`demo-account-${i}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-stone-900">{acc.label}</h3>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 font-medium">{acc.role}</span>
                                </div>
                                <button
                                    onClick={() => handleLogin(acc.email, acc.password)}
                                    disabled={loading}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors disabled:opacity-50"
                                    data-testid={`demo-login-${i}`}
                                >
                                    <LogIn className="w-3.5 h-3.5" />
                                    Login
                                </button>
                            </div>
                            <p className="text-xs text-stone-400 mb-3">{acc.desc}</p>
                            <div className="space-y-2 bg-stone-50 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-stone-500">Email</span>
                                    <div className="flex items-center gap-1.5">
                                        <code className="text-xs font-mono text-stone-700 bg-white px-2 py-0.5 rounded border border-stone-200">{acc.email}</code>
                                        <button onClick={() => copyToClipboard(acc.email)} className="p-1 hover:bg-stone-200 rounded transition-colors" data-testid={`copy-email-${i}`}>
                                            <Copy className="w-3 h-3 text-stone-400" />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-stone-500">Password</span>
                                    <div className="flex items-center gap-1.5">
                                        <code className="text-xs font-mono text-stone-700 bg-white px-2 py-0.5 rounded border border-stone-200">
                                            {showPasswords ? acc.password : '••••••••'}
                                        </code>
                                        <button onClick={() => copyToClipboard(acc.password)} className="p-1 hover:bg-stone-200 rounded transition-colors" data-testid={`copy-pw-${i}`}>
                                            <Copy className="w-3 h-3 text-stone-400" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6">
                    <div className="relative mb-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-stone-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="bg-[#FAFAF5] px-3 text-stone-400">or</span>
                        </div>
                    </div>
                    <button
                        onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/authorize`; }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors shadow-sm"
                    >
                        <LogIn className="w-4 h-4" />
                        Sign in with Multiplai
                    </button>
                </div>

                <div className="mt-4 text-center">
                    <button onClick={() => setShowPasswords(!showPasswords)} className="text-xs text-stone-400 hover:text-stone-600 transition-colors inline-flex items-center gap-1" data-testid="toggle-passwords">
                        {showPasswords ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {showPasswords ? 'Hide' : 'Show'} passwords
                    </button>
                </div>
            </div>
        </div>
    );
}
